import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminHeaders } from '../utils/adminHeaders';
import {
    Typography,
    Sheet,
    Card,
    CardContent,
    Divider,
    Grid,
    Box,
    Chip,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    Radio,
    Select,
    Option,
    IconButton,
    Stack,
    Alert,
    CircularProgress,
    AspectRatio,
    Badge,
    Tooltip,
    Switch,
    FormControl,
    FormHelperText,
} from '@mui/joy';
import {
    DeviceHub,
    Update,
    Settings,
    CameraAlt,
    History,
    CheckCircle,
    Warning,
    Error,
    Info,
    Schedule,
    Person,
    Memory,
    Storage,
    Refresh,
    ArrowBack,
    Email as EmailIcon,
    Videocam,
    SmartDisplay,
    Psychology,
    FiberManualRecord,
    RecordVoiceOver,
    Visibility,
    Router,
    Build,
    Timeline,
    CloudDownload,
    SystemUpdateAlt,
    RestartAlt,
    Cancel,
    ExpandMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import RecordingTimeline from '../components/RecordingTimeline';
import PermissionWrapper from '../components/PermissionWrapper';
import RestartHubButton from '../components/RestartHubButton';
import HubCommandHistory from '../components/HubCommandHistory';
import HubComponentVersions from '../components/HubComponentVersions';
import FootageSharesSection from '../components/FootageSharesSection';
import UploadRequestDialog from '../components/telemetry/UploadRequestDialog';
import UploadRequestsSection from '../components/telemetry/UploadRequestsSection';
import {
    useUploadRequest,
    isCollectionSessionLocking,
    isCollectionSessionActive,
    resolveSessionEndsAt,
} from '../hooks/useUploadRequest';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

const HubDetailsPage = () => {
    const { hubId } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    


    const [hub, setHub] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState('');
    const [otaSessions, setOtaSessions] = useState([]);
    const [commandHistoryKey, setCommandHistoryKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [otaProgress, setOtaProgress] = useState(null);
    const [otaPollingSessionId, setOtaPollingSessionId] = useState(null);
    const pollingRef = useRef(null);
    const [ownerProfile, setOwnerProfile] = useState(null);

    // OTA detail modal state
    const [otaDetailOpen, setOtaDetailOpen] = useState(false);
    const [otaDetailSession, setOtaDetailSession] = useState(null);
    const [otaDetailUpdates, setOtaDetailUpdates] = useState([]);
    const [otaDetailLoading, setOtaDetailLoading] = useState(false);
    const detailPollingRef = useRef(null);

    // Dataset Donations (verification-tool uploads brokered via the main backend).
    // Loading is gated on the developer scope: the backend returns 403 unless the
    // admin has developer:read (or :write), which we surface as a permission notice.
    const [donations, setDonations] = useState([]);
    const [donationsLoading, setDonationsLoading] = useState(true);
    const [donationsError, setDonationsError] = useState(null);
    const [donationsForbidden, setDonationsForbidden] = useState(false);

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadDialogMode, setUploadDialogMode] = useState('range_upload');
    const [logLevelError, setLogLevelError] = useState(null);

    const upload = useUploadRequest(hubId, session);

    const loadDonations = React.useCallback(async () => {
        if (!session?.accessToken) return;
        try {
            setDonationsLoading(true);
            setDonationsError(null);
            setDonationsForbidden(false);
            const res = await fetch(`${API_BASE}/device/${hubId}/uploads`, {
                headers: {
                    'Authorization': session.accessToken,
                    'Vendor-Id': session.vendorId,
                },
            });
            if (res.status === 403) {
                setDonationsForbidden(true);
                setDonations([]);
                return;
            }
            if (!res.ok) {
                throw new Error(`status ${res.status}`);
            }
            const data = await res.json();
            setDonations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to load dataset donations', err);
            setDonationsError('Failed to load dataset donations.');
            setDonations([]);
        } finally {
            setDonationsLoading(false);
        }
    }, [hubId, session]);

    useEffect(() => {
        loadDonations();
    }, [loadDonations]);

    // Load OTA sessions once hub is known
    const loadOtaSessions = React.useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/device/${hubId}/ota/sessions`, { headers: adminHeaders(session) });
            if (res.ok) {
                const sessions = await res.json();
                console.log('OTA Sessions response:', sessions, 'Type:', typeof sessions, 'IsArray:', Array.isArray(sessions));
                // Ensure sessions is always an array
                setOtaSessions(Array.isArray(sessions) ? sessions : []);
            } else {
                console.log('OTA Sessions request failed with status:', res.status);
                setOtaSessions([]); // Fallback to empty array
            }
        } catch (err) {
            console.error('Failed to load OTA sessions', err);
            setOtaSessions([]); // Fallback to empty array
        }
    }, [hubId, session]);

    // Load hub info and cameras
    useEffect(() => {
        const loadHubData = async () => {
            try {
                setLoading(true);
                setError(null);


                const [hubRes, camerasRes] = await Promise.all([
                    fetch(`${API_BASE}/device/${hubId}`, { headers: adminHeaders(session) }),
                    fetch(`${API_BASE}/device/${hubId}/cameras`, { headers: adminHeaders(session) })
                ]);

                if (!hubRes.ok || !camerasRes.ok) {
                    throw new Error('Failed to load hub data');
                }

                const hubData = await hubRes.json();
                const camerasData = await camerasRes.json();

                setHub(hubData);

                // Fetch owner profile
                if (hubData.owner_id) {
                    try {
                        const ownerRes = await fetch(`${API_BASE}/client/${hubData.owner_id}/profile`, { headers: adminHeaders(session) });
                        if (ownerRes.ok) {
                            const ownerData = await ownerRes.json();
                            setOwnerProfile(ownerData);
                        }
                    } catch (err) {
                        console.error('Failed to load owner profile', err);
                    }
                }

                // Ensure cameras is always an array
                const safeCamerasData = Array.isArray(camerasData) ? camerasData : [];
                
                // Load camera history
                const camerasWithHistory = await Promise.all(
                    safeCamerasData.map(async (cam) => {
                        try {
                            const historyRes = await fetch(`${API_BASE}/camera/${cam.id}/history`, {
                                headers: {
                                    'Authorization': session?.accessToken,
                                    'Vendor-ID': session?.vendorId,
                                }
                            });
                            const history = historyRes.ok ? await historyRes.json() : [];
                            // Ensure history is always an array
                            return { ...cam, history: Array.isArray(history) ? history : [] };
                        } catch (err) {
                            console.error(`Failed to load history for camera ${cam.id}:`, err);
                            return { ...cam, history: [] };
                        }
                    })
                );

                setCameras(camerasWithHistory);
            } catch (err) {
                console.error('Failed to load hub data:', err);
                setError('Failed to load hub information. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadHubData();
    }, [hubId, session]);

    const getOtaCategoryColor = (status) => {
        switch (status) {
            case "pending":
                return { color: "warning", variant: "soft" };
            case "started":
                return { color: "primary", variant: "soft" };
            case "failed":
                return { color: "danger", variant: "soft" };
            case "succeed":
                return { color: "success", variant: "soft" };
            case "canceled":
                return { color: "neutral", variant: "soft" };
            default:
                return { color: "neutral", variant: "soft" };
        }
    };

    const getOtaIcon = (status) => {
        switch (status) {
            case "pending":
                return <Schedule />;
            case "started":
                return <Update />;
            case "failed":
                return <Error />;
            case "succeed":
                return <CheckCircle />;
            case "canceled":
                return <Info />;
            default:
                return <Info />;
        }
    };

    const getGranularStatusLabel = (status) => {
        switch (status) {
            case 'pending': return 'Pending';
            case 'started': return 'Started';
            case 'pulling': return 'Pulling images';
            case 'downloaded': return 'Images downloaded';
            case 'installing': return 'Installing';
            case 'rebooting': return 'Verifying services';
            case 'succeed': return 'Completed';
            case 'failed': return 'Failed';
            case 'canceled': return 'Canceled';
            default: return status;
        }
    };

    const getGranularStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'started': return 'primary';
            case 'pulling': return 'primary';
            case 'downloaded': return 'primary';
            case 'installing': return 'primary';
            case 'rebooting': return 'primary';
            case 'succeed': return 'success';
            case 'failed': return 'danger';
            case 'canceled': return 'neutral';
            default: return 'neutral';
        }
    };

    const getGranularStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <Schedule sx={{ fontSize: 18 }} />;
            case 'started': return <Update sx={{ fontSize: 18 }} />;
            case 'pulling': return <CloudDownload sx={{ fontSize: 18 }} />;
            case 'downloaded': return <CheckCircle sx={{ fontSize: 18 }} />;
            case 'installing': return <SystemUpdateAlt sx={{ fontSize: 18 }} />;
            case 'rebooting': return <RestartAlt sx={{ fontSize: 18 }} />;
            case 'succeed': return <CheckCircle sx={{ fontSize: 18 }} />;
            case 'failed': return <Error sx={{ fontSize: 18 }} />;
            case 'canceled': return <Cancel sx={{ fontSize: 18 }} />;
            default: return <Info sx={{ fontSize: 18 }} />;
        }
    };

    const isOtaSessionActive = (session) => {
        return session.status_category !== 'succeed' &&
            session.status_category !== 'failed' &&
            session.status_category !== 'canceled';
    };

    const fetchOtaDetail = async (sessionId) => {
        try {
            const res = await fetch(`${API_BASE}/device/ota/${sessionId}/updates`, { headers: adminHeaders(session) });
            if (res.ok) {
                const data = await res.json();
                setOtaDetailUpdates(data.updates || []);
                // Update status_category from polled data
                if (data.status_category) {
                    setOtaDetailSession(prev => prev ? { ...prev, status_category: data.status_category } : prev);
                    // If terminal state, stop polling
                    if (['succeed', 'failed', 'canceled'].includes(data.status_category)) {
                        if (detailPollingRef.current) {
                            clearInterval(detailPollingRef.current);
                            detailPollingRef.current = null;
                        }
                        loadOtaSessions();
                    }
                }
            }
        } catch (err) {
            console.error('Failed to fetch OTA detail', err);
        }
    };

    const openOtaDetail = (session) => {
        setOtaDetailSession(session);
        setOtaDetailUpdates([]);
        setOtaDetailLoading(true);
        setOtaDetailOpen(true);

        fetchOtaDetail(session.id).then(() => setOtaDetailLoading(false));

        if (isOtaSessionActive(session)) {
            detailPollingRef.current = setInterval(() => fetchOtaDetail(session.id), 2000);
        }
    };

    const closeOtaDetail = () => {
        setOtaDetailOpen(false);
        if (detailPollingRef.current) {
            clearInterval(detailPollingRef.current);
            detailPollingRef.current = null;
        }
    };

    // Clean up detail polling on unmount
    useEffect(() => {
        return () => {
            if (detailPollingRef.current) {
                clearInterval(detailPollingRef.current);
            }
        };
    }, []);

    const parseDetail = (detailStr) => {
        if (!detailStr) return null;
        try {
            return JSON.parse(detailStr);
        } catch {
            return detailStr;
        }
    };

    // Helper: get active OTA session (not succeed, failed, canceled)
    const getActiveOtaSession = () => {
        if (!otaSessions || otaSessions.length === 0) return null;
        const activeSessions = otaSessions.filter(session =>
            session.status_category !== 'succeed' &&
            session.status_category !== 'failed' &&
            session.status_category !== 'canceled'
        );
        activeSessions.sort((a, b) => b.created_at - a.created_at);
        return activeSessions.length > 0 ? activeSessions[0] : null;
    };

    // Helper functions for sync status table
    const formatCreatedDate = (timestamp) => {
        if (!timestamp || timestamp === 0) return "Not yet";
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const formatSyncDateTime = (timestamp) => {
        if (!timestamp || timestamp === 0) return "Not yet";
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    const getTimeDifference = (timestamp) => {
        if (!timestamp || timestamp === 0) return { text: "Not yet", color: "neutral" };
        
        const now = Date.now();
        const diffMs = now - timestamp;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMinutes / 60);

        let text;
        if (diffMinutes < 60) {
            text = `${diffMinutes}min`;
        } else {
            const remainingMinutes = diffMinutes % 60;
            text = remainingMinutes > 0 ? `${diffHours}h ${remainingMinutes}min` : `${diffHours}h`;
        }

        // Color coding based on time difference
        let color;
        if (diffHours < 2) {
            color = "success"; // green
        } else if (diffHours < 8) {
            color = "warning"; // yellow
        } else {
            color = "danger"; // red
        }

        return { text, color };
    };

    const getSyncStatusColor = (timestamp) => {
        if (!timestamp || timestamp === 0) return "neutral";
        return getTimeDifference(timestamp).color;
    };

    // Poll OTA progress if there is an active session
    useEffect(() => {
        const activeSession = getActiveOtaSession();
        if (activeSession) {
            if (otaPollingSessionId !== activeSession.id) {
                setOtaPollingSessionId(activeSession.id);
            }
        } else {
            setOtaPollingSessionId(null);
            setOtaProgress(null);
        }
    }, [otaSessions]);

    useEffect(() => {
        if (!otaPollingSessionId) {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            return;
        }
        // Polling function
        const fetchOtaProgress = async () => {
            try {
                const res = await fetch(`${API_BASE}/device/ota/${otaPollingSessionId}/updates`, { headers: adminHeaders(session) });
                if (res.ok) {
                    const progress = await res.json();
                    setOtaProgress(progress);
                    // Handle completion states
                    if (["succeed", "failed", "canceled"].includes(progress.status_category)) {
                        // Reload OTA sessions immediately
                        await loadOtaSessions();
                        
                        if (progress.status_category === "canceled") {
                            // For canceled sessions, stop polling immediately
                            setOtaPollingSessionId(null);
                        } else {
                            // For succeed/failed sessions, wait 2 seconds before showing "No active OTA"
                            setTimeout(() => {
                                setOtaPollingSessionId(null);
                            }, 2000);
                        }
                    }
                }
            } catch (err) {
                // Optionally handle error
            }
        };
        fetchOtaProgress(); // Initial fetch
        pollingRef.current = setInterval(fetchOtaProgress, 2000);
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [otaPollingSessionId, loadOtaSessions]);


    useEffect(() => {
        if (!hub) return;
        loadOtaSessions();
    }, [hub, loadOtaSessions]);

    // Load OTA candidates once hub is known
    useEffect(() => {
        if (!hub) return;
        const kind = hub.device_kind || 'hub-v1';
        const url = `${API_BASE}/device/${hubId}/ota/candidates?approved_only=true`;

        fetch(url, { headers: adminHeaders(session) })
            .then(res => res.ok ? res.json() : Promise.reject(res.status))
            .then(candidates => {
                console.log('OTA Candidates response:', candidates, 'Type:', typeof candidates, 'IsArray:', Array.isArray(candidates));
                // Ensure candidates is always an array
                setCandidates(Array.isArray(candidates) ? candidates : []);
            })
            .catch(err => {
                console.error('Failed to load OTA candidates', err);
                setCandidates([]); // Fallback to empty array
            });
    }, [hub, hubId]);

    // Update hub patch helper
    const patchHub = async (patch) => {
        try {
            const res = await fetch(`${API_BASE}/device/${hubId}`, {
                method: 'PATCH',
                headers: adminHeaders(session, { 'Content-Type': 'application/json' }),
                body: JSON.stringify(patch),
            });
            
            if (!res.ok) {
                const err = new Error('Failed to update hub');
                err.status = res.status;
                throw err;
            }

            // Only update local state for non-firmware updates
            // Firmware version should only be updated after successful OTA
            if (!patch.firmware_version) {
                setHub(prev => ({ ...prev, ...patch }));
            }
        } catch (err) {
            console.error('Failed to patch hub', err);
            throw err;
        }
    };

    // Dialog confirm handler
    const handleUpdateFirmware = async () => {
        if (!selectedCandidate) return;
        
        try {
            await patchHub({ firmware_version: selectedCandidate });
            setUpdateDialogOpen(false);
            setTimeout(loadOtaSessions, 1000);
        } catch (err) {
            console.error('Failed to update firmware:', err);
        }
    };

    const handleLogLevelChange = async (event, newValue) => {
        try {
            setLogLevelError(null);
            await patchHub({ log_level: newValue });
        } catch (err) {
            console.error('Failed to update log level:', err);
            if (err?.status === 409) {
                setLogLevelError('Log level is locked by an active collection session.');
            } else {
                setLogLevelError('Failed to update log level.');
            }
        }
    };

    const openUploadDialog = (mode = 'range_upload') => {
        setUploadDialogMode(mode);
        setUploadDialogOpen(true);
    };

    if (loading) {
        return (
            <Sheet sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size="lg" />
                    <Typography level="body-lg">Loading hub details...</Typography>
                </Stack>
            </Sheet>
        );
    }

    if (error) {
        return (
            <Sheet sx={{ p: 4 }}>
                <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                    <Typography>{error}</Typography>
                </Alert>
                <Button variant="outlined" onClick={() => window.location.reload()}>
                    <Refresh sx={{ mr: 1 }} />
                    Retry
                </Button>
            </Sheet>
        );
    }

    const logLevelLocked = isCollectionSessionLocking(upload.job, hub, upload.jobs);
    const sessionActive = isCollectionSessionActive(upload.job, hub, upload.jobs);
    const sessionEndsAt = resolveSessionEndsAt(upload.job);
    const lockUntilLabel = sessionEndsAt != null
        ? new Date(sessionEndsAt).toLocaleString()
        : null;

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <IconButton 
                        variant="outlined" 
                        onClick={() => navigate('/admin')}
                        sx={{ borderRadius: '50%' }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <DeviceHub sx={{ fontSize: 32, color: 'primary.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            {hub?.name || `Hub ${hubId}`}
                        </Typography>
                        <Typography level="body-sm" textColor="neutral.500">
                            Hardware ID: {hub?.hardware_id}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {hub && (
                <>
                    {/* Main Hub Information */}
                    <Card variant="outlined" sx={{ mb: 4 }}>
                        <CardContent>
                            <Grid container spacing={3}>
                                {/* Hub Details */}
                                <Grid xs={12}>
                                    <Stack spacing={3}>
                                        {/* Basic Information */}
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography level="h4" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Settings sx={{ mr: 1 }} />
                                                    Hub Configuration
                                                </Typography>
                                            </Box>
                                            
                                            <Grid container spacing={2}>
                                                <Grid xs={12} sm={6}>
                                                    <Card variant="soft" sx={{ p: 2 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                            <Person color="primary" />
                                                            <Typography level="body-sm" fontWeight="bold">
                                                                {ownerProfile ? `Owner ID: ${ownerProfile.id}` : 'Owner ID'}
                                                            </Typography>
                                                        </Stack>
                                                        {ownerProfile && (
                                                            <Stack spacing={0.5} sx={{ pl: 4 }}>
                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                    <EmailIcon sx={{ fontSize: 18, color: 'neutral.700' }} />
                                                                    <Typography level="body-sm">{ownerProfile.email}</Typography>
                                                                </Stack>
                                                            </Stack>
                                                        )}
                                                    </Card>
                                                </Grid>
                                                
                                                <Grid xs={12} sm={6}>
                                                    <Card variant="soft" sx={{ p: 2 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                            <DeviceHub color="primary" />
                                                            <Typography level="body-sm" fontWeight="bold">Hub ID</Typography>
                                                        </Stack>
                                                        <Typography level="body-lg">{hub.id}</Typography>
                                                    </Card>
                                                </Grid>
                                            </Grid>
                                            
                                            {/* Row 2: Firmware and OTA Status (merged) */}
                                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                                {/* Left column: Firmware, Log Level, Created */}
                                                <Grid xs={12} sm={6}>
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography level="title-md" sx={{ mb: 1 }}>Firmware</Typography>
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Chip
                                                                size="lg"
                                                                variant="soft"
                                                                color="primary"
                                                                startDecorator={<Memory />}
                                                            >
                                                                {hub.firmware_version || "Not Set"}
                                                            </Chip>
                                                            <Button
                                                                variant="solid"
                                                                startDecorator={<Update />}
                                                                onClick={() => {
                                                                    setSelectedCandidate('');
                                                                    setUpdateDialogOpen(true);
                                                                }}
                                                            >
                                                                Update Firmware
                                                            </Button>
                                                        </Stack>
                                                    </Box>
                                                    <PermissionWrapper requiredPermission="device:write">
                                                        <Box sx={{ mb: 3 }}>
                                                            <Typography level="title-md" sx={{ mb: 1 }}>Maintenance</Typography>
                                                            <RestartHubButton
                                                                hubId={hubId}
                                                                onStatusChange={() => setCommandHistoryKey((k) => k + 1)}
                                                            />
                                                        </Box>
                                                    </PermissionWrapper>
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography level="title-md" sx={{ mb: 1 }}>Log Level</Typography>
                                                        <FormControl>
                                                            <Select
                                                                value={hub.log_level}
                                                                onChange={handleLogLevelChange}
                                                                disabled={logLevelLocked}
                                                            >
                                                                {['trace', 'debug', 'info', 'warning', 'error'].map(lvl => (
                                                                    <Option key={lvl} value={lvl}>
                                                                        {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                            {logLevelLocked && (
                                                                <FormHelperText>
                                                                    {lockUntilLabel
                                                                        ? `Locked by collection session until ${lockUntilLabel}.`
                                                                        : 'Locked by collection session.'}
                                                                </FormHelperText>
                                                            )}
                                                        </FormControl>
                                                        {logLevelError && (
                                                            <Alert color="danger" variant="soft" sx={{ mt: 1 }}>
                                                                {logLevelError}
                                                            </Alert>
                                                        )}
                                                    </Box>
                                                    
                                                    {/* AI Enabled Toggle */}
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography level="title-md" sx={{ mb: 1 }}>AI Processing</Typography>
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Switch
                                                                checked={hub.ai_enabled || false}
                                                                disabled={true}
                                                                color={hub.ai_enabled ? "success" : "neutral"}
                                                            />
                                                            <Typography level="body-sm" textColor={hub.ai_enabled ? "success.500" : "neutral.500"}>
                                                                {hub.ai_enabled ? "Enabled" : "Disabled"}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                    
                                                    {/* Recording Enabled Toggle */}
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography level="title-md" sx={{ mb: 1 }}>Recording</Typography>
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Switch
                                                                checked={hub.recording_enabled || false}
                                                                disabled={true}
                                                                color={hub.recording_enabled ? "success" : "neutral"}
                                                            />
                                                            <Typography level="body-sm" textColor={hub.recording_enabled ? "success.500" : "neutral.500"}>
                                                                {hub.recording_enabled ? "Enabled" : "Disabled"}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>

                                                    <Grid xs={12} sm={6}>
                                                        <Typography level="body-sm" textColor="neutral.500">
                                                            Created: {new Date(hub.created_at).toLocaleString()}
                                                        </Typography>
                                                    </Grid>


                                                </Grid>
                                                                                {/* Right column: Sync Status Table */}
                                <Grid xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                                        <Typography level="title-md">Sync Status</Typography>
                                    </Box>
                                    <Card variant="soft" sx={{ p: 2 }}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 1.5, alignItems: 'center' }}>
                                            {/* Created At */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                Created At:
                                            </Typography>
                                            <Typography level="body-sm">
                                                {formatCreatedDate(hub.created_at)}
                                            </Typography>
                                            <Chip size="sm" variant="soft" color="neutral">
                                                {hub.created_at ? "–" : "Not yet"}
                                            </Chip>

                                            {/* Settings Sync At */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                Settings Sync At:
                                            </Typography>
                                            <Typography level="body-sm">
                                                {formatSyncDateTime(hub.settings_synced_at)}
                                            </Typography>
                                            <Chip size="sm" variant="soft" color={getSyncStatusColor(hub.settings_synced_at)}>
                                                {getTimeDifference(hub.settings_synced_at).text}
                                            </Chip>

                                            {/* Cameras Sync up To */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                Cameras Sync up To:
                                            </Typography>
                                            <Typography level="body-sm">
                                                {formatSyncDateTime(hub.devices_synced_at)}
                                            </Typography>
                                            <Chip size="sm" variant="soft" color={getSyncStatusColor(hub.devices_synced_at)}>
                                                {getTimeDifference(hub.devices_synced_at).text}
                                            </Chip>

                                            {/* Recordings Sync up To */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                Recordings Sync up To:
                                            </Typography>
                                            <Typography level="body-sm">
                                                {formatSyncDateTime(hub.recordings_synced_at)}
                                            </Typography>
                                            <Chip size="sm" variant="soft" color={getSyncStatusColor(hub.recordings_synced_at)}>
                                                {getTimeDifference(hub.recordings_synced_at).text}
                                            </Chip>

                                            {/* Events Sync up To */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                Events Sync up To:
                                            </Typography>
                                            <Typography 
                                                level="body-sm" 
                                                sx={{ 
                                                    cursor: 'pointer', 
                                                    color: 'primary.500',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                                onClick={() => navigate(`/hub/${hubId}/events`)}
                                            >
                                                {formatSyncDateTime(hub.events_synced_at)}
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip size="sm" variant="soft" color={getSyncStatusColor(hub.events_synced_at)}>
                                                    {getTimeDifference(hub.events_synced_at).text}
                                                </Chip>
                                                <IconButton 
                                                    size="sm" 
                                                    variant="soft" 
                                                    color="primary"
                                                    onClick={() => navigate(`/hub/${hubId}/events`)}
                                                >
                                                    <Timeline sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>

                                            {/* Resource Utilization Sync up To */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                Resource Utilization Sync up To:
                                            </Typography>
                                            <Typography 
                                                level="body-sm" 
                                                sx={{ 
                                                    cursor: 'pointer', 
                                                    color: 'primary.500',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                                onClick={() => navigate(`/hub/${hubId}/telemetry`)}
                                            >
                                                {formatSyncDateTime(hub.resource_utilization_synced_at)}
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip size="sm" variant="soft" color={getSyncStatusColor(hub.resource_utilization_synced_at)}>
                                                    {getTimeDifference(hub.resource_utilization_synced_at).text}
                                                </Chip>
                                                <IconButton 
                                                    size="sm" 
                                                    variant="soft" 
                                                    color="primary"
                                                    onClick={() => navigate(`/hub/${hubId}/telemetry`)}
                                                >
                                                    <Timeline sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>

                                            {/* AI Telemetry Sync up To */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                AI Telemetry Sync up To:
                                            </Typography>
                                            <Typography
                                                level="body-sm"
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.500',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                                onClick={() => navigate(`/hub/${hubId}/telemetry`)}
                                            >
                                                {formatSyncDateTime(hub.ai_telemetry_synced_at)}
                                            </Typography>
                                            <Chip size="sm" variant="soft" color={getSyncStatusColor(hub.ai_telemetry_synced_at)}>
                                                {getTimeDifference(hub.ai_telemetry_synced_at).text}
                                            </Chip>

                                            {/* Logs Sync up To */}
                                            <Typography level="body-sm" fontWeight="bold">
                                                Logs Sync up To:
                                            </Typography>
                                            <Typography level="body-sm">
                                                {formatSyncDateTime(hub.logs_synced_at)}
                                            </Typography>
                                            <Chip size="sm" variant="soft" color={getSyncStatusColor(hub.logs_synced_at)}>
                                                {getTimeDifference(hub.logs_synced_at).text}
                                            </Chip>
                                        </Box>
                                    </Card>
                                </Grid>
                                            </Grid>
                                        </Box>



                                        
                                    </Stack>
                                </Grid>


                            </Grid>
                        </CardContent>
                    </Card>

                    <UploadRequestsSection
                        jobs={upload.jobs}
                        loading={upload.listLoading}
                        error={upload.error}
                        onNewRequest={openUploadDialog}
                        onRequestAgain={(item) => openUploadDialog(
                            item?.type === 'collection_session' ? 'collection_session' : 'range_upload'
                        )}
                    />

                    {/* Connected Cameras Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography level="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                            <CameraAlt sx={{ mr: 1 }} />
                            Connected Cameras
                            <Badge badgeContent={(cameras || []).length} color="primary" sx={{ ml: 2 }}>
                                <Box />
                            </Badge>
                        </Typography>

                        {(!cameras || !Array.isArray(cameras) || cameras.length === 0) ? (
                            <Card variant="soft" sx={{ p: 4, textAlign: 'center' }}>
                                <Typography level="h4" sx={{ mb: 1 }}>No Cameras Connected</Typography>
                                <Typography level="body-sm" textColor="neutral.500">
                                    This hub doesn't have any cameras connected yet.
                                </Typography>
                            </Card>
                        ) : (
                            <Grid container spacing={4}>
                                {(cameras || []).map((cam) => {
                                    // Parse ONVIF settings to extract device details
                                    let onvifData = {};
                                    try {
                                        if (cam.onvif_settings) {
                                            onvifData = JSON.parse(JSON.parse(cam.onvif_settings));
                                        }
                                    } catch (e) {
                                        console.warn('Failed to parse ONVIF settings for camera', cam.id);
                                    }

                                    // Parse video capabilities
                                    let videoCaps = {};
                                    try {
                                        if (cam.caps) {
                                            videoCaps = JSON.parse(cam.caps);
                                        }
                                    } catch (e) {
                                        console.warn('Failed to parse video capabilities for camera', cam.id);
                                    }

                                    // Get the best available resolution
                                    const getBestResolution = () => {
                                        if (videoCaps.video?.h265?.length > 0) {
                                            const profile = videoCaps.video.h265[0];
                                            return `${profile.width}x${profile.height} @ ${profile.fps}fps (H.265)`;
                                        }
                                        if (videoCaps.video?.h264?.length > 0) {
                                            const profile = videoCaps.video.h264[0];
                                            return `${profile.width}x${profile.height} @ ${profile.fps}fps (H.264)`;
                                        }
                                        return 'Unknown';
                                    };

                                    return (
                                        <Grid key={cam.id} xs={12} md={6} lg={4}>
                                            <Card variant="outlined" sx={{ 
                                                height: '460px', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}>
                                                <CardContent sx={{ flex: 1, p: 2 }}>
                                                    <Stack spacing={2}>
                                                        {/* Camera Header */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Tooltip title={cam.device_name || onvifData.model_name || `Camera ${cam.hub_camera_id}`} arrow>
                                                                    <Typography 
                                                                        level="title-md" 
                                                                        sx={{ 
                                                                            mb: 0.5,
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            cursor: 'help'
                                                                        }}
                                                                    >
                                                                        {cam.device_name || onvifData.model_name || `Camera ${cam.hub_camera_id}`}
                                                                    </Typography>
                                                                </Tooltip>
                                                                <Typography level="body-sm" textColor="neutral.500">
                                                                    {onvifData.manufacturer_name || cam.device_type?.toUpperCase() || 'Unknown'}
                                                                </Typography>
                                                                <Tooltip title={`Hardware ID: ${cam.hardware_id}`} arrow>
                                                                    <Typography 
                                                                        level="body-xs" 
                                                                        textColor="neutral.400"
                                                                        sx={{
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            cursor: 'help'
                                                                        }}
                                                                    >
                                                                        HW ID: {cam.hardware_id}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </Box>
                                                            <Stack spacing={0.5} alignItems="flex-end">
                                                                <Chip size="sm" variant="soft" color="success">
                                                                    Connected
                                                                </Chip>
                                                                <Typography level="body-xs" textColor="neutral.500">
                                                                    Global ID: {cam.id}
                                                                </Typography>
                                                                <Typography level="body-xs" textColor="neutral.500">
                                                                    On-hub ID: {cam.hub_camera_id}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>

                                                        <Divider />

                                                        {/* Status Indicators */}
                                                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                                            <Chip 
                                                                size="sm" 
                                                                variant="soft" 
                                                                color={cam.recording_enabled ? "success" : "neutral"}
                                                                startDecorator={<Videocam sx={{ fontSize: 12 }} />}
                                                            >
                                                                Rec {cam.recording_enabled ? 'ON' : 'OFF'}
                                                            </Chip>
                                                            <Chip 
                                                                size="sm" 
                                                                variant="soft" 
                                                                color={cam.ai_enabled ? "primary" : "neutral"}
                                                                startDecorator={<SmartDisplay sx={{ fontSize: 12 }} />}
                                                            >
                                                                AI {cam.ai_enabled ? 'ON' : 'OFF'}
                                                            </Chip>
                                                            <Chip 
                                                                size="sm" 
                                                                variant="soft" 
                                                                color={cam.live_monitoring_enabled ? "warning" : "neutral"}
                                                                startDecorator={<Visibility sx={{ fontSize: 12 }} />}
                                                            >
                                                                Live {cam.live_monitoring_enabled ? 'ON' : 'OFF'}
                                                            </Chip>
                                                        </Stack>

                                                        {/* Technical Details - Compact */}
                                                        <Stack spacing={0.5}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Typography level="body-xs" fontWeight="bold" textColor="neutral.600">
                                                                    Resolution:
                                                                </Typography>
                                                                <Typography level="body-xs">
                                                                    {getBestResolution()}
                                                                </Typography>
                                                            </Stack>

                                                            {onvifData.ip_address && (
                                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                    <Typography level="body-xs" fontWeight="bold" textColor="neutral.600">
                                                                        Network:
                                                                    </Typography>
                                                                    <Typography level="body-xs">
                                                                        {onvifData.ip_address}
                                                                    </Typography>
                                                                </Stack>
                                                            )}

                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Typography level="body-xs" fontWeight="bold" textColor="neutral.600">
                                                                    Log Level:
                                                                </Typography>
                                                                <Chip size="sm" variant="outlined" color="neutral">
                                                                    {cam.log_level?.toUpperCase() || 'INFO'}
                                                                </Chip>
                                                            </Stack>
                                                        </Stack>

                                                        {/* Recording Timeline */}
                                                        <Divider />
                                                        <Box>
                                                            <RecordingTimeline recordings={cam.history} />
                                                        </Box>

                                                        {/* Footer with timestamps */}
                                                        <Divider />
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                            <Typography level="body-xs" textColor="neutral.400">
                                                                Added: {new Date(cam.created_at).toLocaleDateString()}
                                                            </Typography>
                                                            {cam.updated_at !== cam.created_at && (
                                                                <Typography level="body-xs" textColor="neutral.400">
                                                                    Updated: {new Date(cam.updated_at).toLocaleDateString()}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Box>

                    <PermissionWrapper requiredPermissions={['device:read', 'device:write']}>
                        <HubComponentVersions hubId={hubId} />
                    </PermissionWrapper>

                    <PermissionWrapper requiredPermissions={['device:read', 'device:write']}>
                        <HubCommandHistory hubId={hubId} refreshKey={commandHistoryKey} />
                    </PermissionWrapper>

                    {/* OTA Sessions Section - Moved after Connected Cameras */}
                    <Box sx={{ mb: 4 }}>
                        <Typography level="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                            <History sx={{ mr: 1 }} />
                            OTA Sessions
                            <Badge badgeContent={(otaSessions || []).length} color="primary" sx={{ ml: 2 }}>
                                <Box />
                            </Badge>
                        </Typography>
                        
                        {(!otaSessions || !Array.isArray(otaSessions) || otaSessions.length === 0) ? (
                            <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                                <Info sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                                <Typography level="body-sm" textColor="neutral.500">
                                    No OTA sessions found
                                </Typography>
                            </Card>
                        ) : (
                            <Grid container spacing={3}>
                                {(otaSessions || []).map(session => (
                                    <Grid key={session.id} xs={12} md={6} lg={4}>
                                        <Card variant="outlined" sx={{ p: 3, height: '100%' }}>
                                            <Stack spacing={2}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography level="title-md" fontWeight="bold">
                                                        {session.from_fw_version || '–'} → {session.to_fw_version || '–'}
                                                    </Typography>
                                                    <Chip
                                                        size="sm"
                                                        startDecorator={getOtaIcon(session.status_category)}
                                                        {...getOtaCategoryColor(session.status_category)}
                                                    >
                                                        {session.status_category}
                                                    </Chip>
                                                </Box>
                                                
                                                <Typography level="body-sm" textColor="neutral.600">
                                                    Flow: {session.ota_flow} | Created by: {session.created_by_id}
                                                </Typography>
                                                
                                                <Typography level="body-sm" textColor="neutral.500">
                                                    Created: {new Date(session.created_at * 1000).toLocaleString()}
                                                    {session.updated_at !== session.created_at && (
                                                        <>
                                                            <br />
                                                            Updated: {new Date(session.updated_at * 1000).toLocaleString()}
                                                        </>
                                                    )}
                                                </Typography>

                                                <Typography
                                                    level="body-sm"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        color: 'primary.500',
                                                        fontWeight: 500,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                    onClick={() => openOtaDetail(session)}
                                                >
                                                    More details
                                                    <ExpandMore sx={{ fontSize: 16 }} />
                                                </Typography>
                                            </Stack>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>

                    {/* Dataset Donations Section - verification-tool uploads (developer scope) */}
                    <Box sx={{ mb: 4 }}>
                        <Typography level="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                            <CloudDownload sx={{ mr: 1 }} />
                            Dataset Donations
                            {!donationsForbidden && (
                                <Badge badgeContent={(donations || []).length} color="primary" sx={{ ml: 2 }}>
                                    <Box />
                                </Badge>
                            )}
                            {!donationsForbidden && (
                                <Tooltip title="Reload donations">
                                    <IconButton
                                        size="sm"
                                        variant="outlined"
                                        sx={{ ml: 'auto' }}
                                        onClick={loadDonations}
                                        disabled={donationsLoading}
                                    >
                                        <Refresh />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Typography>

                        {donationsForbidden ? (
                            <Alert color="warning" variant="soft" startDecorator={<Warning />}>
                                <Box>
                                    <Typography level="title-sm">Developer access required</Typography>
                                    <Typography level="body-sm" textColor="neutral.600">
                                        You need Developer Access (read or write) to view dataset donations.
                                        Ask an administrator to grant the “Developer Access” permission.
                                    </Typography>
                                </Box>
                            </Alert>
                        ) : donationsLoading ? (
                            <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                                <CircularProgress size="sm" />
                            </Card>
                        ) : donationsError ? (
                            <Alert color="danger" variant="soft">
                                {donationsError}
                            </Alert>
                        ) : (!donations || donations.length === 0) ? (
                            <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                                <Info sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                                <Typography level="body-sm" textColor="neutral.500">
                                    No dataset donations from this hub yet
                                </Typography>
                            </Card>
                        ) : (
                            <Grid container spacing={3}>
                                {donations.map(ds => (
                                    <Grid key={ds.dataset_id} xs={12} md={6} lg={4}>
                                        <Card variant="outlined" sx={{ p: 3, height: '100%' }}>
                                            <Stack spacing={1.5}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                    <Typography level="title-md" fontWeight="bold" noWrap>
                                                        {ds.name || ds.slug}
                                                    </Typography>
                                                    <Chip size="sm" variant="soft" color={ds.status === 'frozen' ? 'success' : 'neutral'}>
                                                        {ds.status}
                                                    </Chip>
                                                </Box>

                                                <Typography level="body-sm" textColor="neutral.600">
                                                    Uploaded: {ds.uploaded_at ? new Date(ds.uploaded_at).toLocaleString() : '–'}
                                                </Typography>

                                                {(ds.selected_from_ms && ds.selected_to_ms) && (
                                                    <Typography level="body-sm" textColor="neutral.500">
                                                        Clip: {new Date(ds.selected_from_ms).toLocaleString()} → {new Date(ds.selected_to_ms).toLocaleString()}
                                                    </Typography>
                                                )}

                                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                    <Chip size="sm" variant="outlined" startDecorator={<Storage sx={{ fontSize: 14 }} />}>
                                                        {Number(ds.size_mb || 0).toFixed(1)} MB
                                                    </Chip>
                                                    <Chip size="sm" variant="outlined">
                                                        ID: {ds.dataset_id}
                                                    </Chip>
                                                    {ds.client_id && (
                                                        <Chip size="sm" variant="outlined" startDecorator={<Person sx={{ fontSize: 14 }} />}>
                                                            client {ds.client_id}
                                                        </Chip>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>

                    {/* Footage Shares Section - owner-consented uploads to the verification tool (developer scope) */}
                    <PermissionWrapper requiredPermissions={['developer:read', 'developer:write']}>
                        <FootageSharesSection hubId={hubId} />
                    </PermissionWrapper>

                    {/* OTA Detail Modal */}
                    <Modal open={otaDetailOpen} onClose={closeOtaDetail}>
                        <ModalDialog size="lg" sx={{ maxWidth: 640, maxHeight: '85vh', overflow: 'auto' }}>
                            <DialogTitle>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Timeline />
                                    <Typography>OTA Session #{otaDetailSession?.id}</Typography>
                                    {otaDetailSession && (
                                        <Chip
                                            size="sm"
                                            startDecorator={getOtaIcon(otaDetailSession.status_category)}
                                            {...getOtaCategoryColor(otaDetailSession.status_category)}
                                        >
                                            {otaDetailSession.status_category}
                                        </Chip>
                                    )}
                                    {otaDetailSession && isOtaSessionActive(otaDetailSession) && (
                                        <CircularProgress size="sm" sx={{ ml: 'auto' }} />
                                    )}
                                </Stack>
                            </DialogTitle>
                            <DialogContent>
                                {otaDetailSession && (
                                    <Stack spacing={3}>
                                        {/* Session summary */}
                                        <Card variant="soft" sx={{ p: 2 }}>
                                            <Stack spacing={1}>
                                                <Typography level="title-sm">
                                                    {otaDetailSession.from_fw_version || '–'} → {otaDetailSession.to_fw_version || '–'}
                                                </Typography>
                                                <Typography level="body-xs" textColor="neutral.500">
                                                    Flow: {otaDetailSession.ota_flow} | Created: {new Date(otaDetailSession.created_at * 1000).toLocaleString()}
                                                </Typography>
                                            </Stack>
                                        </Card>

                                        {/* Updates timeline */}
                                        {otaDetailLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : otaDetailUpdates.length === 0 ? (
                                            <Typography level="body-sm" textColor="neutral.500" sx={{ textAlign: 'center', py: 2 }}>
                                                No status updates yet
                                            </Typography>
                                        ) : (
                                            <Stack spacing={0}>
                                                {otaDetailUpdates.map((update, idx) => {
                                                    const detail = parseDetail(update.detail);
                                                    const isLast = idx === otaDetailUpdates.length - 1;
                                                    const statusColor = getGranularStatusColor(update.to_state);

                                                    return (
                                                        <Box key={update.id} sx={{ display: 'flex', gap: 2 }}>
                                                            {/* Timeline line + dot */}
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 28 }}>
                                                                <Box sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    borderRadius: '50%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    bgcolor: `${statusColor}.softBg`,
                                                                    color: `${statusColor}.500`,
                                                                    flexShrink: 0,
                                                                }}>
                                                                    {getGranularStatusIcon(update.to_state)}
                                                                </Box>
                                                                {!isLast && (
                                                                    <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'neutral.200', minHeight: 16 }} />
                                                                )}
                                                            </Box>

                                                            {/* Content */}
                                                            <Box sx={{ pb: isLast ? 0 : 2, flexGrow: 1, minWidth: 0 }}>
                                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                                                    <Typography level="title-sm">
                                                                        {getGranularStatusLabel(update.to_state)}
                                                                    </Typography>
                                                                    <Typography level="body-xs" textColor="neutral.400">
                                                                        {new Date(update.created_at).toLocaleTimeString()}
                                                                    </Typography>
                                                                </Stack>

                                                                {detail && typeof detail === 'object' && (
                                                                    <Card variant="soft" size="sm" sx={{ p: 1.5, mt: 0.5 }}>
                                                                        <Stack spacing={0.5}>
                                                                            {detail.service && (
                                                                                <Typography level="body-xs">
                                                                                    <strong>Service:</strong> {detail.service}
                                                                                </Typography>
                                                                            )}
                                                                            {detail.image && (
                                                                                <Typography level="body-xs" sx={{ wordBreak: 'break-all' }}>
                                                                                    <strong>Image:</strong> {detail.image}
                                                                                </Typography>
                                                                            )}
                                                                            {detail.from_image && (
                                                                                <Typography level="body-xs" sx={{ wordBreak: 'break-all' }}>
                                                                                    <strong>From:</strong> {detail.from_image}
                                                                                </Typography>
                                                                            )}
                                                                            {detail.status && (
                                                                                <Chip size="sm" variant="soft" color={
                                                                                    detail.status === 'pulled' ? 'success' :
                                                                                    detail.status === 'failed' ? 'danger' :
                                                                                    detail.status === 'skipped' ? 'neutral' : 'primary'
                                                                                }>
                                                                                    {detail.status}
                                                                                </Chip>
                                                                            )}
                                                                            {detail.info && (
                                                                                <Typography level="body-xs" textColor="neutral.500">
                                                                                    {detail.info}
                                                                                </Typography>
                                                                            )}
                                                                            {detail.error && (
                                                                                <Alert size="sm" color="danger" variant="soft">
                                                                                    <Typography level="body-xs">{detail.error}</Typography>
                                                                                </Alert>
                                                                            )}
                                                                            {detail.updates && Array.isArray(detail.updates) && (
                                                                                <Stack spacing={0.5}>
                                                                                    {detail.updates.map((u, i) => (
                                                                                        <Typography key={i} level="body-xs">
                                                                                            {u.service}: {u.from || '–'} → {u.to || '–'}
                                                                                        </Typography>
                                                                                    ))}
                                                                                </Stack>
                                                                            )}
                                                                            {detail.skipped && Array.isArray(detail.skipped) && detail.skipped.length > 0 && (
                                                                                <Typography level="body-xs" textColor="neutral.500">
                                                                                    Skipped: {detail.skipped.map(s => s.service || s).join(', ')}
                                                                                </Typography>
                                                                            )}
                                                                            {detail.cleanup && (
                                                                                <Typography level="body-xs" textColor="neutral.500">
                                                                                    Cleanup: {detail.cleanup.images_removed} images removed
                                                                                    {detail.cleanup.space_reclaimed && ` (${detail.cleanup.space_reclaimed})`}
                                                                                </Typography>
                                                                            )}
                                                                            {detail.duration_s && (
                                                                                <Typography level="body-xs" textColor="neutral.500">
                                                                                    Duration: {detail.duration_s.toFixed(1)}s
                                                                                </Typography>
                                                                            )}
                                                                            {detail.pulled !== undefined && (
                                                                                <Typography level="body-xs">
                                                                                    Pulled: {detail.pulled}/{detail.total}
                                                                                    {detail.skipped_count > 0 && `, Skipped: ${detail.skipped_count}`}
                                                                                </Typography>
                                                                            )}
                                                                        </Stack>
                                                                    </Card>
                                                                )}

                                                                {detail && typeof detail === 'string' && (
                                                                    <Typography level="body-xs" textColor="neutral.500" sx={{ mt: 0.5 }}>
                                                                        {detail}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button variant="plain" onClick={closeOtaDetail}>
                                    Close
                                </Button>
                            </DialogActions>
                        </ModalDialog>
                    </Modal>

                    {/* Firmware Update Dialog */}
                    <Modal open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)}>
                        <ModalDialog size="lg">
                            <DialogTitle>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Update />
                                    <Typography>Select Firmware Version</Typography>
                                </Stack>
                            </DialogTitle>
                            <DialogContent>
                                {(!candidates || !Array.isArray(candidates) || candidates.length === 0) ? (
                                    <Alert color="warning" variant="soft">
                                        <Typography>No firmware candidates available for this hub.</Typography>
                                    </Alert>
                                ) : (
                                    <RadioGroup
                                        value={selectedCandidate}
                                        onChange={(event) => setSelectedCandidate(event.target.value)}
                                    >
                                        <Stack spacing={2}>
                                            {(candidates || []).map(c => (
                                                <Card key={c.id} variant="soft" sx={{ p: 2 }}>
                                                    <Radio
                                                        value={c.version_name}
                                                        label={
                                                            <Box sx={{ ml: 1 }}>
                                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                                    <Typography level="body-lg" fontWeight="bold">
                                                                        {c.version_name}
                                                                    </Typography>
                                                                    <Chip
                                                                        size="sm"
                                                                        variant="soft"
                                                                        color={c.approval_status === 'approved' ? 'success' : 'warning'}
                                                                    >
                                                                        {c.approval_status}
                                                                    </Chip>
                                                                </Stack>
                                                                {c.release_notes && (
                                                                    <Typography level="body-sm" textColor="neutral.600">
                                                                        {c.release_notes}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                </Card>
                                            ))}
                                        </Stack>
                                    </RadioGroup>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button variant="plain" onClick={() => setUpdateDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="solid"
                                    disabled={!selectedCandidate}
                                    onClick={handleUpdateFirmware}
                                    startDecorator={<Update />}
                                >
                                    Update Firmware
                                </Button>
                            </DialogActions>
                        </ModalDialog>
                    </Modal>

                </>
            )}

            <UploadRequestDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                onSubmit={upload.submit}
                submitting={upload.submitting}
                initialMode={uploadDialogMode}
                sessionActive={sessionActive}
            />
        </Sheet>
    );
};

export default HubDetailsPage;
